import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpService } from '../../core/services/http/http-service';
import { UserHttpService } from '../../core/services/http/user-http-service';
import { CustomersHttpService } from '../../core/services/http/customers-http-service';
import { Order, OrderStatus, OrderType } from '../../core/models/order';
import { Customer } from '../../core/models/customer';
import { User, UserCategory } from '../../core/models/user';
import { ORDER_CATEGORIES, ORDER_STATUSES, ORDER_TYPES } from '../../core/constants';
import { debounceTime, distinctUntilChanged, of, Subject, switchMap } from 'rxjs';

@Component({
  selector: 'app-orders',
  standalone: false,
  templateUrl: './orders.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './orders.css',
})
export class Orders implements OnInit {
  protected readonly statuses = ORDER_STATUSES;
  protected readonly types = ORDER_TYPES;
  protected readonly categoryTabs = ORDER_CATEGORIES;

  protected orders: Array<Order> | null = null;
  protected error: string | null = null;
  protected actioningOrder: Order | null = null;
  private userNames: Map<string, string> | null = null;

  // filters
  protected orderId = '';
  protected selectedStatus: OrderStatus | '' = '';
  protected selectedType: OrderType | '' = '';
  protected orderCategory: UserCategory | null = null;
  protected from = '';
  protected to = '';

  protected selectedCustomer: Customer | null = null;
  protected customerOptions: Customer[] | null = null;
  private readonly customerSearch$ = new Subject<string>();

  protected selectedRep: User | null = null;
  protected repOptions: User[] | null = null;
  private readonly repSearch$ = new Subject<string>();

  private readonly orderIdSearch$ = new Subject<void>();

  protected page = 0;
  protected pageSize = 10;
  protected totalPages = 0;
  protected totalElements = 0;

  constructor(
    private readonly httpService: HttpService,
    private readonly userHttpService: UserHttpService,
    private readonly customersHttpService: CustomersHttpService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.customerSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => (query ? this.customersHttpService.searchCustomers(query) : of(null))),
        takeUntilDestroyed(),
      )
      .subscribe((customers) => {
        this.customerOptions = customers;
        this.cdr.detectChanges();
      });

    this.repSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((email) => (email ? this.userHttpService.searchReps(email) : of(null))),
        takeUntilDestroyed(),
      )
      .subscribe((reps) => {
        this.repOptions = reps;
        this.cdr.detectChanges();
      });

    this.orderIdSearch$
      .pipe(debounceTime(400), takeUntilDestroyed())
      .subscribe(() => this.onFilterChange());
  }

  ngOnInit() {
    this.userHttpService.getAllUsers().subscribe({
      next: (users) => {
        this.userNames = new Map(users.map((user) => [user.id, user.name]));
        this.cdr.detectChanges();
      },
      error: () => {},
    });
    this.loadOrders();
  }

  protected loadOrders() {
    this.error = null;
    this.httpService
      .getOrders({
        page: this.page,
        size: this.pageSize,
        id: this.orderId.trim(),
        status: this.selectedStatus,
        type: this.selectedType,
        category: this.orderCategory ?? '',
        customerId: this.selectedCustomer?.id,
        repId: this.selectedRep?.id,
        lastUpdatedAfter: Orders.startOfDay(this.from),
        lastUpdatedBefore: Orders.startOfNextDay(this.to),
      })
      .subscribe({
        next: (page) => {
          this.orders = page.content;
          this.totalPages = page.page.totalPages;
          this.totalElements = page.page.totalElements;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.orders = [];
          this.error = this.extractError(err, 'Failed to load orders');
          this.cdr.detectChanges();
        },
      });
  }

  protected onFilterChange() {
    this.page = 0;
    this.loadOrders();
  }

  protected clearFilters() {
    this.orderId = '';
    this.selectedStatus = '';
    this.selectedType = '';
    this.orderCategory = null;
    this.from = '';
    this.to = '';
    this.selectCustomerFilter(null);
    this.selectRepFilter(null);
  }

  protected goToPage(page: number) {
    this.page = page;
    this.loadOrders();
  }

  protected onPageSizeChange(size: number) {
    this.pageSize = size;
    this.onFilterChange();
  }

  protected selectCategory(category: UserCategory | null) {
    this.orderCategory = category;
    this.onFilterChange();
  }

  protected onOrderIdSearch() {
    this.orderIdSearch$.next();
  }

  protected onCustomerSearch(query: string) {
    this.customerSearch$.next(query.trim());
  }

  protected selectCustomerFilter(customer: Customer | null) {
    this.selectedCustomer = customer;
    this.customerOptions = null;
    this.onFilterChange();
  }

  protected onRepSearch(email: string) {
    this.repSearch$.next(email.trim());
  }

  protected selectRepFilter(rep: User | null) {
    this.selectedRep = rep;
    this.repOptions = null;
    this.onFilterChange();
  }

  protected resolveUserName(userId: string): string {
    if (userId === 'system') return 'system';
    return this.userNames?.get(userId) ?? userId;
  }

  protected showOrderInfo(order: Order) {
    this.actioningOrder = order;
    this.cdr.detectChanges();
  }

  protected onActioningOrderDeselected() {
    this.actioningOrder = null;
    this.loadOrders();
  }

  /** No zone suffix: the server binds `LocalDateTime`, which rejects a trailing `Z`. */
  private static startOfDay(date: string): string | undefined {
    return date ? `${date}T00:00:00` : undefined;
  }

  /**
   * `lastUpdatedBefore` is a strict `<`, so the bound is the next day — otherwise the same
   * From and To date matches nothing.
   */
  private static startOfNextDay(date: string): string | undefined {
    if (!date) return undefined;
    const next = new Date(`${date}T00:00:00`);
    next.setDate(next.getDate() + 1);
    // local-time parts, not toISOString(), which would shift the date across the UTC offset
    const month = `${next.getMonth() + 1}`.padStart(2, '0');
    const day = `${next.getDate()}`.padStart(2, '0');
    return `${next.getFullYear()}-${month}-${day}T00:00:00`;
  }

  private extractError(err: unknown, fallback: string): string {
    const message = (err as { error?: { message?: string } })?.error?.message;
    return message || fallback;
  }
}
